import React, { useLayoutEffect, useRef } from 'react';

/**
 * A checkbox that can also show the half-ticked state, which can only be set from script.
 * Set in a layout effect so it never paints in the wrong state first.
 */
const TriStateCheckbox = ({ checked, indeterminate, ...props }) => {
    const ref = useRef(null);
    useLayoutEffect(() => {
        if (ref.current) {
            ref.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);
    return <input ref={ref} type="checkbox" checked={checked} {...props} />;
};

const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '2px 16px',
    listStyle: 'none',
    paddingLeft: 0,
    margin: 0,
};

const fieldsetStyle = { border: '1px solid #ccc', padding: '8px 12px', marginBottom: '12px' };

/**
 * Ticks or clears a set of documents together: ticked when all of them are, half-ticked
 * when only some are. Ticking it ticks the rest; clearing it clears them all.
 */
const GroupCheckbox = ({ id, label, memberIDs, selectedIDs, disabled, onChange }) => {
    const ticked = memberIDs.filter((documentID) => selectedIDs.has(documentID)).length;
    const all = ticked === memberIDs.length;
    return (
        <>
            <TriStateCheckbox
                id={id}
                checked={all}
                indeterminate={ticked > 0 && !all}
                disabled={disabled}
                onChange={() => onChange(memberIDs, !all)}
            />
            {' '}
            <label htmlFor={id}>{label}</label>
        </>
    );
};

/**
 * The documents one role will be subscribed to, as a grid of checkboxes like the user
 * Profile > Reports tab. Changes only go to the server when the page's Save is pressed.
 *
 * With groups, a "Report groups" box above the grid has one checkbox per group plus
 * "Other Reports" for every document in none of them. Without groups (solicitors, who
 * only have a few documents) there's a single "Select all / none" checkbox instead.
 * (For now ClientDocumentSubscriptions doesn't pass documents in no group, so "Other
 * Reports" doesn't appear; this may be temporary.)
 *
 * Saved documents the role isn't allowed (non-solicitor documents set up for solicitors
 * before the rule) are
 * listed so the user knows Save will remove them.
 */
const SubscriptionChecklist = ({
    role,
    documents,
    groups,
    selectedIDs,
    notAllowed,
    disabled,
    onChange,
}) => {
    const roleName = role.roleTypeDescription.toLowerCase();
    const headingID = `client-doc-subs-role-${role.roleTypeID}`;

    const groupsWithMembers = groups
        .map((g) => ({
            key: String(g.group),
            description: g.description,
            memberIDs: documents.filter((d) => d.group === g.group).map((d) => d.documentID),
        }))
        .filter((g) => g.memberIDs.length > 0);

    if (groupsWithMembers.length > 0) {
        const shownGroups = new Set(groups.map((g) => g.group));
        const otherIDs = documents
            .filter((d) => !shownGroups.has(d.group))
            .map((d) => d.documentID);
        if (otherIDs.length > 0) {
            groupsWithMembers.push({ key: 'other', description: 'Other Reports', memberIDs: otherIDs });
        }
    }

    const allIDs = documents.map((d) => d.documentID);

    return (
        <section aria-labelledby={headingID} style={{ marginBottom: '24px' }}>
            <h3 id={headingID}>New {roleName}s receive</h3>

            {groupsWithMembers.length > 0 && (
                <fieldset style={fieldsetStyle}>
                    <legend>Report groups for new {roleName}s</legend>
                    <ul style={gridStyle}>
                        {groupsWithMembers.map((g) => (
                            <li key={g.key}>
                                <GroupCheckbox
                                    id={`client-doc-subs-${role.roleTypeID}-group-${g.key}`}
                                    label={g.description}
                                    memberIDs={g.memberIDs}
                                    selectedIDs={selectedIDs}
                                    disabled={disabled}
                                    onChange={onChange}
                                />
                            </li>
                        ))}
                    </ul>
                </fieldset>
            )}

            <fieldset style={fieldsetStyle}>
                <legend>Reports for new {roleName}s</legend>
                {groupsWithMembers.length === 0 && documents.length > 0 && (
                    <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>
                        <GroupCheckbox
                            id={`client-doc-subs-${role.roleTypeID}-all`}
                            label="Select all / none"
                            memberIDs={allIDs}
                            selectedIDs={selectedIDs}
                            disabled={disabled}
                            onChange={onChange}
                        />
                    </div>
                )}
                {documents.length === 0 ? (
                    <p>No documents found.</p>
                ) : (
                    <ul style={gridStyle}>
                        {documents.map((d) => {
                            const checkboxID = `client-doc-subs-${role.roleTypeID}-doc-${d.documentID}`;
                            return (
                                <li key={d.documentID}>
                                    <input
                                        id={checkboxID}
                                        type="checkbox"
                                        checked={selectedIDs.has(d.documentID)}
                                        disabled={disabled}
                                        onChange={(e) => onChange([d.documentID], e.target.checked)}
                                    />
                                    {' '}
                                    <label htmlFor={checkboxID}>{d.documentDescription}</label>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </fieldset>

            {notAllowed.length > 0 && (
                <div role="note">
                    <p>These documents aren&apos;t allowed for {roleName}s and will be removed when you save:</p>
                    <ul>
                        {notAllowed.map((s) => (
                            <li key={s.clientDocumentSubscriptionID}>
                                {s.documentDescription} ({s.documentID})
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};

export default SubscriptionChecklist;
